import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { drawQueryKeys } from '../../../../lib/entry.js';
/**
 * Observes the Tanstack draw query cache for a given (product, drawNumber) and returns the latest
 * `drawState.client`. Active wager cards use this to rerender when draw state changes (e.g. when a
 * draw transitions from OPEN to LIVE) without issuing a network request themselves.
 *
 * The query is `enabled: false` and reads exclusively from the cache; values are expected to be
 * seeded server-side via `prefetchActiveWagerListHydration` and refreshed client-side via the
 * draw subscription pipeline.
 *
 * @param args hook arguments
 * @param args.product draw product (e.g. Powerplay)
 * @param args.drawNumber draw number to observe
 * @param args.initialDrawStateClient fallback value used until the cache has data
 * @returns latest draw state for the client, or `initialDrawStateClient` when cache is empty
 */
export function useDrawStateClient(t0) {
  const $ = _c(13);
  const {
    product,
    drawNumber,
    initialDrawStateClient
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== drawNumber || $[1] !== product) {
    t1 = drawQueryKeys.draw(product, drawNumber);
    $[0] = drawNumber;
    $[1] = product;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const drawQueryKey = t1;
  let t2;
  if ($[3] !== drawQueryKey || $[4] !== queryClient) {
    t2 = () => queryClient.getQueryData(drawQueryKey);
    $[3] = drawQueryKey;
    $[4] = queryClient;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== drawQueryKey || $[7] !== queryClient) {
    t3 = () => queryClient.getQueryData(drawQueryKey);
    $[6] = drawQueryKey;
    $[7] = queryClient;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== drawQueryKey || $[10] !== t2 || $[11] !== t3) {
    t4 = {
      queryKey: drawQueryKey,
      queryFn: t2,
      enabled: false,
      initialData: t3,
      select: _temp
    };
    $[9] = drawQueryKey;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const {
    data
  } = useQuery(t4);
  return data ?? initialDrawStateClient;
}
function _temp(drawData) {
  return drawData?.draw.drawState.client;
}